/**
 * Authored **rooms** — a purely-visual map primitive and the rect geometry behind it.
 *
 * Load-bearing invariant for everything in this module: **rooms carry no mechanical
 * meaning.** No LoS, movement, or cover effect derives from a room; the reducer stores
 * and restores rects opaquely and never reads `x`/`y`/`w`/`h` to make a geometry or
 * grid-mode decision. The `(x, y, w, h)` shape is square-cell-shaped because the
 * authoring surface is 2d-square — it is data the render/authoring layer interprets,
 * not something the grid-agnostic engine branches on.
 */

/** uuid identifying a {@link Room} within an encounter. */
export type RoomId = string;

/**
 * One axis-aligned rectangle of an authored {@link Room}, in **cell** coordinates:
 * min-corner `(x, y)` plus `w` columns × `h` rows (each `>= 1`). A room is a *list*
 * of these (a union of rects), so an L-/T-shaped room is several overlapping or
 * abutting boxes. Plain JSON, no class — the same wire/persist invariant as the rest
 * of this file.
 */
export type RoomRect = {
  /** Min-corner column. */
  x: number;
  /** Min-corner row. */
  y: number;
  /** Width in cells (`>= 1`). */
  w: number;
  /** Height in cells (`>= 1`). */
  h: number;
};

/**
 * An authored **room** — a named **union of rectangles** the GM drag-boxes onto the
 * board. Purely visual: rooms exist for the holo "glowing floor + name + volume" art
 * direction only, and the engine never reads them. Stored id-addressed in
 * {@link EncounterState.rooms}. Plain JSON, no class.
 */
export type Room = {
  id: RoomId;
  /** Display name — auto-assigned `ROOM 1`, `ROOM 2`, … at authoring, GM-renamable. */
  name: string;
  /** The room's footprint as a union of axis-aligned rects (in cell coords). */
  rects: RoomRect[];
};

/** RAW volume of one map cell: a 3 m cube = 27 m³ (Lancer 1 space = 3 m). */
export const ROOM_CELL_VOLUME_M3 = 27;

/** Whether cell `(x, y)` falls inside `rect` (min-corner inclusive, far edge exclusive). */
export function rectContainsCell(rect: RoomRect, x: number, y: number): boolean {
  return x >= rect.x && x < rect.x + rect.w && y >= rect.y && y < rect.y + rect.h;
}

/** Whether cell `(x, y)` falls inside **any** of a room's rects (the start-cell membership test). */
export function roomContainsCell(room: Room, x: number, y: number): boolean {
  return room.rects.some((r) => rectContainsCell(r, x, y));
}

/**
 * The number of **distinct floor cells** a room covers — the union of its rects, so
 * overlapping/abutting boxes never double-count a cell. The basis for the volume label
 * (`floorCells × {@link ROOM_CELL_VOLUME_M3}`). Pure; the render label reads it.
 */
export function roomFloorCells(room: Room): number {
  const cells = new Set<string>();
  for (const r of room.rects) {
    for (let dx = 0; dx < r.w; dx++) {
      for (let dy = 0; dy < r.h; dy++) cells.add(`${r.x + dx},${r.y + dy}`);
    }
  }
  return cells.size;
}

/** A room's volume in m³ — its distinct {@link roomFloorCells} times {@link ROOM_CELL_VOLUME_M3}. */
export function roomVolume(room: Room): number {
  return roomFloorCells(room) * ROOM_CELL_VOLUME_M3;
}

/** A single unit boundary edge in cell coordinates (endpoints in cell-corner space). */
export type RoomEdge = { x1: number; y1: number; x2: number; y2: number };

/**
 * The boundary outline of a room's union of rects, as unit edges in cell coords.
 * Interior edges (shared by two filled cells) cancel, leaving only the silhouette —
 * so the render can stroke ONE merged outline instead of per-rect borders. Pure.
 */
export function roomBoundaryEdges(room: Room): RoomEdge[] {
  const filled = new Set<string>();
  for (const r of room.rects) {
    for (let dx = 0; dx < r.w; dx++) {
      for (let dy = 0; dy < r.h; dy++) filled.add(`${r.x + dx},${r.y + dy}`);
    }
  }
  const edges = new Map<string, RoomEdge>();
  // Toggle a unit edge: add if absent (boundary), remove if present (interior between two cells).
  // Canonical key orders endpoints so both adjacent cells produce the same key and cancel.
  const toggle = ({ x1, y1, x2, y2 }: RoomEdge) => {
    const key = x1 < x2 || (x1 === x2 && y1 <= y2) ? `${x1},${y1},${x2},${y2}` : `${x2},${y2},${x1},${y1}`;
    if (edges.has(key)) edges.delete(key);
    else edges.set(key, { x1, y1, x2, y2 });
  };
  for (const cell of filled) {
    const [x, y] = cell.split(",").map(Number) as [number, number];
    toggle({ x1: x, y1: y, x2: x + 1, y2: y }); // top
    toggle({ x1: x + 1, y1: y, x2: x + 1, y2: y + 1 }); // right
    toggle({ x1: x, y1: y + 1, x2: x + 1, y2: y + 1 }); // bottom
    toggle({ x1: x, y1: y, x2: x, y2: y + 1 }); // left
  }
  return [...edges.values()];
}

/**
 * **Rect-set difference, re-compressed to a clean `RoomRect[]`** — the load-bearing
 * geometry helper behind both no-overlap enforcement (a new
 * box carves the contested cells out of every other room) and the on-canvas room erase
 * (an Alt-drag subtracts its cells from whatever room(s) it hits). Given a room's `rects`
 * (a union, possibly overlapping) and a set of cell keys (`"x,y"`, the same convention as
 * {@link roomFloorCells}) to remove, it computes the **surviving** cell set and covers it
 * with a minimal-ish list of fresh, non-overlapping rects via a **greedy row-major
 * rectangle cover**: repeatedly take the top-left surviving cell, grow it right as far as
 * the row stays filled, then grow that strip downward as far as every column stays filled,
 * emit that rect, and remove its cells. Properties:
 *
 *  - **Carve-to-zero** ⇒ `[]` (every cell removed — the caller turns this into a `remove_room`).
 *  - **A hole in the middle** of one rect splits it into up to **4** rects (the top strip, the
 *    left and right columns flanking the hole, the bottom strip) — never leaving the hole covered.
 *  - **A no-op carve** (no removed key touches the room) returns a cover of the *same* cells —
 *    re-compressed, so it may have fewer rects than the input but covers exactly the original area.
 *
 * Purely positional — like every other room geometry function here it never consults a rule
 * seam. Pure + deterministic.
 */
export function subtractRoomCells(rects: RoomRect[], removed: Set<string>): RoomRect[] {
  // The surviving cell set: every cell of the union, minus the removed keys.
  const alive = new Set<string>();
  for (const r of rects) {
    for (let dy = 0; dy < r.h; dy++) {
      for (let dx = 0; dx < r.w; dx++) {
        const key = `${r.x + dx},${r.y + dy}`;
        if (!removed.has(key)) alive.add(key);
      }
    }
  }
  return greedyRectCover(alive);
}

/**
 * Cover a set of cell keys (`"x,y"`) with a minimal-ish list of non-overlapping, axis-aligned
 * {@link RoomRect}s — the re-compression half of {@link subtractRoomCells}. Greedy, row-major:
 * each pass finds the top-left remaining cell, grows the rect right while the row stays filled,
 * then grows it down while every column of that width stays filled, emits it, and removes its
 * cells. Deterministic (top-left-first), so the output is stable for a given input.
 */
function greedyRectCover(cells: Set<string>): RoomRect[] {
  const remaining = new Set(cells);
  const has = (x: number, y: number): boolean => remaining.has(`${x},${y}`);
  const rects: RoomRect[] = [];
  while (remaining.size > 0) {
    // The top-left remaining cell (min row, then min column) anchors the next rect.
    let ax = Infinity;
    let ay = Infinity;
    for (const key of remaining) {
      const ci = key.indexOf(",");
      const x = Number(key.slice(0, ci));
      const y = Number(key.slice(ci + 1));
      if (y < ay || (y === ay && x < ax)) {
        ax = x;
        ay = y;
      }
    }
    // Grow right while the anchor row stays filled.
    let w = 1;
    while (has(ax + w, ay)) w++;
    // Grow down while every column [ax, ax+w) of the next row stays filled.
    let h = 1;
    for (;;) {
      const ny = ay + h;
      let full = true;
      for (let dx = 0; dx < w; dx++) {
        if (!has(ax + dx, ny)) {
          full = false;
          break;
        }
      }
      if (!full) break;
      h++;
    }
    rects.push({ x: ax, y: ay, w, h });
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) remaining.delete(`${ax + dx},${ay + dy}`);
    }
  }
  return rects;
}
